import React, { useState, useRef } from 'react';
import LexicalRichTextareaEditor from '../../forms/components/LexicalRichTextareaEditor';

interface WorkExperience {
  id?: number;
  position?: string;
  company?: string;
  start_month?: string;
  start_year?: string;
  end_month?: string;
  end_year?: string;
  current?: boolean;
  description?: string;
  _destroy?: boolean;
}

interface Props {
  workExperiences?: WorkExperience[];
  onWorkExperiencesChange?: (experiences: WorkExperience[]) => void;
}

const ProfileExperience: React.FC<Props> = ({
  workExperiences = [],
  onWorkExperiencesChange
}) => {
  const [experiences, setExperiences] = useState<WorkExperience[]>(workExperiences);
  const [openAccordions, setOpenAccordions] = useState<{ [key: number]: boolean }>({});
  const newExperienceRef = useRef<HTMLDivElement>(null);

  const handleExperienceChange = (index: number, field: keyof WorkExperience, value: any) => {
    const updatedExperiences = experiences.map((exp, i) =>
      i === index ? { ...exp, [field]: value } : exp
    );
    setExperiences(updatedExperiences);
    onWorkExperiencesChange?.(updatedExperiences);
  };

  const addNewExperience = () => {
    const newExperience: WorkExperience = {
      position: '',
      company: '',
      start_month: '',
      start_year: '',
      end_month: '',
      end_year: '',
      current: false,
      description: ''
    };

    const updatedExperiences = [...experiences, newExperience];
    setExperiences(updatedExperiences);
    onWorkExperiencesChange?.(updatedExperiences);

    const newIndex = updatedExperiences.length - 1;
    setOpenAccordions(prev => ({ ...prev, [newIndex]: true }));

    setTimeout(() => {
      newExperienceRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const deleteExperience = (index: number) => {
    if (window.confirm('Are you sure you want to delete this work experience?')) {
      const updatedExperiences = experiences.filter((_, i) => i !== index);
      setExperiences(updatedExperiences);
      onWorkExperiencesChange?.(updatedExperiences);

      // Update accordion states
      const newOpenAccordions: { [key: number]: boolean } = {};
      Object.keys(openAccordions).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newOpenAccordions[keyIndex] = openAccordions[keyIndex];
        } else if (keyIndex > index) {
          newOpenAccordions[keyIndex - 1] = openAccordions[keyIndex];
        }
      });
      setOpenAccordions(newOpenAccordions);
    }
  };

  const moveExperienceUp = (index: number) => {
    if (index === 0) return;

    const updatedExperiences = [...experiences];
    [updatedExperiences[index - 1], updatedExperiences[index]] =
      [updatedExperiences[index], updatedExperiences[index - 1]];

    setExperiences(updatedExperiences);
    onWorkExperiencesChange?.(updatedExperiences);

    // Update accordion states
    const newOpenAccordions = { ...openAccordions };
    const tempState = newOpenAccordions[index - 1];
    newOpenAccordions[index - 1] = newOpenAccordions[index];
    newOpenAccordions[index] = tempState;
    setOpenAccordions(newOpenAccordions);
  };

  const moveExperienceDown = (index: number) => {
    if (index === experiences.length - 1) return;

    const updatedExperiences = [...experiences];
    [updatedExperiences[index], updatedExperiences[index + 1]] =
      [updatedExperiences[index + 1], updatedExperiences[index]];

    setExperiences(updatedExperiences);
    onWorkExperiencesChange?.(updatedExperiences);

    // Update accordion states
    const newOpenAccordions = { ...openAccordions };
    const tempState = newOpenAccordions[index];
    newOpenAccordions[index] = newOpenAccordions[index + 1];
    newOpenAccordions[index + 1] = tempState;
    setOpenAccordions(newOpenAccordions);
  };

  const toggleAccordion = (index: number) => {
    setOpenAccordions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getExperienceTitle = (experience: WorkExperience) => {
    if (experience.position && experience.company) {
      return `${experience.position} at ${experience.company}`;
    } else if (experience.position) {
      return experience.position;
    } else if (experience.company) {
      return experience.company;
    }
    return 'New Work Experience';
  };

  const monthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let year = currentYear; year >= 1950; year--) {
    yearOptions.push({ value: year.toString(), label: year.toString() });
  }

  return (
    <div className="profile-experience">
      <div className="form-section">
        <h3 className="form-section-title">Work Experience</h3>

        {experiences.map((experience, index) => (
          <div
            key={index}
            className="card mb-3"
            ref={index === experiences.length - 1 ? newExperienceRef : null}
          >
            <div className="card-header" id={`heading-experience-${index}`}>
              <h5 className="mb-0">
                <button
                  className="btn btn-link"
                  type="button"
                  onClick={() => toggleAccordion(index)}
                  aria-expanded={openAccordions[index] || false}
                  aria-controls={`collapse-experience-${index}`}
                >
                  {getExperienceTitle(experience)}
                  <i className={`fas ${openAccordions[index] ? 'fa-chevron-up' : 'fa-chevron-down'} float-right`}></i>
                </button>
              </h5>
              <div className="card-header-actions">
                {index > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => moveExperienceUp(index)}
                    title="Move up"
                  >
                    <i className="fas fa-arrow-up"></i>
                  </button>
                )}
                {index < experiences.length - 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => moveExperienceDown(index)}
                    title="Move down"
                  >
                    <i className="fas fa-arrow-down"></i>
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteExperience(index)}
                  title="Delete"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {openAccordions[index] && (
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Position Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={experience.position || ''}
                        onChange={(e) => handleExperienceChange(index, 'position', e.target.value)}
                        placeholder="e.g., Dental Assistant"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Company</label>
                      <input
                        type="text"
                        className="form-control"
                        value={experience.company || ''}
                        onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                        placeholder="e.g., Bright Smile Dental"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Start Month</label>
                      <select
                        className="form-control"
                        value={experience.start_month || ''}
                        onChange={(e) => handleExperienceChange(index, 'start_month', e.target.value)}
                      >
                        <option value="">Select Month</option>
                        {monthOptions.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Start Year</label>
                      <select
                        className="form-control"
                        value={experience.start_year || ''}
                        onChange={(e) => handleExperienceChange(index, 'start_year', e.target.value)}
                      >
                        <option value="">Select Year</option>
                        {yearOptions.map(year => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>End Month</label>
                      <select
                        className="form-control"
                        value={experience.end_month || ''}
                        onChange={(e) => handleExperienceChange(index, 'end_month', e.target.value)}
                        disabled={experience.current}
                      >
                        <option value="">Select Month</option>
                        {monthOptions.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>End Year</label>
                      <select
                        className="form-control"
                        value={experience.end_year || ''}
                        onChange={(e) => handleExperienceChange(index, 'end_year', e.target.value)}
                        disabled={experience.current}
                      >
                        <option value="">Select Year</option>
                        {yearOptions.map(year => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`current-${index}`}
                      checked={experience.current || false}
                      onChange={(e) => {
                        handleExperienceChange(index, 'current', e.target.checked);
                        if (e.target.checked) {
                          handleExperienceChange(index, 'end_month', '');
                          handleExperienceChange(index, 'end_year', '');
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor={`current-${index}`}>
                      I currently work here
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <LexicalRichTextareaEditor
                    editorState={experience.description || ''}
                    onChangeDescription={(value) => handleExperienceChange(index, 'description', value)}
                    placeholder="Describe your responsibilities, achievements, and key skills used in this role..."
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={addNewExperience}
        >
          <i className="fas fa-plus"></i> Add Work Experience
        </button>
      </div>
    </div>
  );
};

export default ProfileExperience;
